/*
 * <slider-nav> -- shared prev/next arrow controller for horizontal sliders.
 *
 * Renders in a section header (snippets/slider-nav.liquid) and drives a scroll
 * track that lives elsewhere in the same section, referenced by id via
 * data-target. Mirrors the arrow behaviour of collection-tab-slider.js, but
 * decoupled from tabs and from <scroll-rail> so the same control works on the
 * bespoke PDP/collection sliders too.
 *
 * Progressive enhancement: arrows are hidden until JS adds .is-ready, and are
 * only revealed (via [data-overflow]) at >=750px AND when the track actually
 * overflows. Below 750px the track is swiped by touch, so the arrows stay hidden.
 * Each arrow scrolls one page (clientWidth) and disables at its end.
 */
if (!customElements.get('slider-nav')) {
  customElements.define(
    'slider-nav',
    class extends HTMLElement {
      connectedCallback() {
        const id = this.dataset.target;
        this.track = id ? document.getElementById(id) : null;
        this.prevBtn = this.querySelector('[data-dir="prev"]');
        this.nextBtn = this.querySelector('[data-dir="next"]');
        if (!this.track || !this.prevBtn || !this.nextBtn) return;

        // 750px = the theme's tablet breakpoint: at/above it there's no touch
        // swipe to rely on, so the arrows earn their place.
        this.mq = window.matchMedia('(min-width: 750px)');
        this.update = this.update.bind(this);

        this.prevBtn.addEventListener('click', () => this.page(-1));
        this.nextBtn.addEventListener('click', () => this.page(1));
        this.track.addEventListener('scroll', this.update, { passive: true });

        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(this.update);
          this.ro.observe(this.track);
        } else {
          window.addEventListener('resize', this.update, { passive: true });
        }
        if (this.mq.addEventListener) this.mq.addEventListener('change', this.update);

        this.update();
        this.classList.add('is-ready');
      }

      disconnectedCallback() {
        if (this.ro) this.ro.disconnect();
      }

      page(direction) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollBy({
          left: direction * this.track.clientWidth,
          behavior: reduce ? 'auto' : 'smooth',
        });
      }

      update() {
        // A track only earns arrows when it is actually scrollable. Checking the
        // computed overflow (not just scrollWidth) rules out static grids at their
        // desktop breakpoint, which can report a few px of spurious overflow from
        // sub-pixel column rounding while overflow-x is visible.
        const overflowX = getComputedStyle(this.track).overflowX;
        const scrollable = overflowX === 'auto' || overflowX === 'scroll';
        const max = this.track.scrollWidth - this.track.clientWidth;
        const overflows = this.mq.matches && scrollable && max > 1;
        this.toggleAttribute('data-overflow', overflows);
        this.prevBtn.disabled = !overflows || this.track.scrollLeft <= 1;
        this.nextBtn.disabled = !overflows || this.track.scrollLeft >= max - 1;
      }
    }
  );
}
